import fetch from 'isomorphic-fetch'
import { captureException } from '@sentry/browser'

import { testEnvHostname } from '@h2/tests/serverHandlers'

const errors = {
  regex: /^(?<method>.+):(?<status>\d+):(?<message>.+)/i,

  /**
   * Creates a standardized error from failed API requests.
   *
   * @param {string} method HTTP method
   * @param {number} status HTTP status
   * @param {string} description Error message, possibly JSON
   * @returns Error
   */
  create(method, status, description) {
    return new Error(`${method}:${status}:${description}`)
  },

  log(error) {
    captureException(error)
  },

  getMethod(error) {
    const matches = error.message.match(this.regex)

    if (matches) {
      return matches.groups.method
    }

    return ''
  },

  getStatus(error) {
    const matches = error.message.match(this.regex)

    if (matches) {
      return parseInt(matches.groups.status)
    }

    return 0
  },

  getMessage(error, asParsed = false) {
    const matches = error.message.match(this.regex)
    let message = ''

    if (matches) {
      try {
        message = JSON.parse(matches.groups.message.trim())
      } catch {
        message = 'An error occured'
      }
    }

    if (asParsed) {
      return message
    }

    // Check for standard non-validation error response data of { message: string } from API
    if (message?.message) {
      message = message.message
    }

    return message.toString()
  },

  /**
   * Returns a validation object in the form of { [field]: <error message> }
   * or `null` if the error status and/or message is not associated with
   * API validation errors.
   *
   * @TODO (API)
   * Use one status code and response format for validation errors from the API.
   * Map upstream validation errors to the same status code and JSON format.
   *
   * @param {object} error Error object generated by this.error()
   * @returns object | null
   */
  getValidation(error) {
    const status = this.getStatus(error)
    const validation = {}

    // Legacy H2 validation error status
    if (status === 402) {
      const msg = this.getMessage(error)

      try {
        JSON.parse(msg).forEach(({ field, error: validationMsg }) => {
          validation[field] = validationMsg
        })

        return validation
      } catch {
        // Unexpected error format from API. Do nothing.
      }
    }

    if (status === 400) {
      const { validationErrors } = this.getMessage(error, true)

      if (validationErrors) {
        Object.keys(validationErrors).forEach(field => {
          validation[field] = validationErrors[field].join(', ')
        })

        return validation
      }
    }

    return null
  },

  getOtherInfo(error) {
    const msg = this.getMessage(error)
    const json = JSON.parse(msg)
    const otherInfo = {}

    if (json.otherInfo) {
      json.otherInfo.forEach(({ key, value }) => {
        otherInfo[key] = value
      })
    }

    return otherInfo
  }
}
const api = {
  async fetch(url, options = {}) {
    const { hostname, headers, body, version, ...init } = options
    let endpoint = `/api/${version ?? 'v2'}${url}`
    let response = null
    let data = null

    init.credentials = 'same-origin'
    init.method = options.method ?? 'GET'
    init.headers = { accept: 'application/json' }

    if (hostname) {
      endpoint = `${hostname.replace(/\/$/, '')}${endpoint}`
    }

    // To Do: Remove this when we have a better solution for testing. Want test configurations in test env files, not production env files
    if (location.href === `${testEnvHostname}/`) {
      endpoint = `${testEnvHostname}${endpoint}`
    }

    if (body) {
      init.body = body

      if (!(body instanceof FormData)) {
        /**
         * Only set Content-Type header with `fetch` when not using `FormData`.
         * @see https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects#sect4
         */
        init.headers['content-type'] = 'application/json'

        if (typeof body === 'object') {
          init.body = JSON.stringify(body)
        }
      }
    }

    init.headers = { ...init.headers, ...headers }

    response = await fetch(endpoint, init)

    try {
      const type = response.headers.get('content-type')

      if (/application\/json/.test(type)) {
        data = await response.json()
      }

      if (/text/.test(type)) {
        data = await response.text()
      }

      if (/image|audio|video|octet-stream/.test(type)) {
        data = await response.blob()
      }
    } catch (err) {
      /**
       * Some status codes have no response body.
       * @see https://github.com/whatwg/fetch/issues/113
       */
      if (response.status !== 204) {
        // Rethrow the error, unexpected response without JSON body.
        throw err
      }
    }

    if (!response.ok) {
      const msg = data ?? response.statusText ?? endpoint
      const error = errors.create(init.method, response.status, JSON.stringify(msg))

      // Only log API errors if the HTTP status starts with 5
      if (/^5/.test(response.status)) {
        errors.log(error)
      }

      throw error
    }

    return data
  }
}

export { api, errors }
